import {
  DropdownField,
  NumberField,
  TanstackFieldWrapper,
} from 'components/form-fields-2';
import { useScenarioForm } from './contexts';
import commonStyles from './common.module.scss';
import styles from './segmentation-configs.module.scss';
import { capitalize } from 'lodash-es';
import { isSegmentationConfigModified } from './utils';
import {
  AccordionDetails,
  Accordion,
  AccordionSummary,
} from 'components/accordion/accordion';
import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, InlineNotification } from '@carbon/react';
import { Reset, Save } from '@carbon/react/icons';
import { createResourceAtom } from 'lib/atoms';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { ConfigSkeleton } from './config-skeleton';
import { scenarioFormDefaultValues } from './default-values';
import { SaveConfigModal } from './save-config-modal';
import { useSaveConfigModal } from './hooks';
import { saveSegmentationConfigAtom } from './atoms';
import { SegmentationConfig } from './types';

const FALLBACK_ATOM = atom(null);

export const SegmentationConfigs = () => {
  const [expanded, setExpanded] = useState(true);
  const [refetchOptions, setRefetchOptions] = useState<number>(0);
  const {
    form,
    defaultValues,
    selectedConfig,
    setFieldBaseline,
    updateFieldDefault,
  } = useScenarioForm();

  const { configModalState, onClose, onOpen, error, setError } =
    useSaveConfigModal();

  const saveSegmentationConfigMutation = useSetAtom(saveSegmentationConfigAtom);

  const segmentationConfigHref = selectedConfig?.segmentationConfig?.href;
  const segmentationConfigResourceHref =
    selectedConfig?.resources.segmentationConfigs?.href;

  const resourceAtom = useMemo(() => {
    if (!segmentationConfigHref) return FALLBACK_ATOM;

    return createResourceAtom<any>(segmentationConfigHref);
  }, [segmentationConfigHref]);

  const configOptionsAtom = useMemo(() => {
    if (!segmentationConfigResourceHref) return FALLBACK_ATOM;

    return createResourceAtom<any>(segmentationConfigResourceHref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentationConfigResourceHref, refetchOptions]);

  const configOptions = useAtomValue(configOptionsAtom);

  const resource = useAtomValue(resourceAtom);

  useEffect(() => {
    if (resource && resource.data) {
      const segmentationConfigData = {
        id: resource.data.id,
        name: resource.data.name,
        method: resource.data.method,
        targetSegment:
          resource.data.targetSegment ??
          scenarioFormDefaultValues.segmentationConfig.targetSegment,
        parameters: {
          ...scenarioFormDefaultValues.segmentationConfig.parameters,
          ...resource.data.parameters,
        },
      };
      setFieldBaseline('segmentationConfig', segmentationConfigData);
    } else if (resource === null && selectedConfig?.id) {
      const fallback = scenarioFormDefaultValues.segmentationConfig;
      setFieldBaseline('segmentationConfig', fallback);
    }
  }, [resource, selectedConfig?.id, setFieldBaseline, form]);

  const formattedOptions = useMemo(() => {
    if (!(configOptions && configOptions.data?.results)) return [];

    return configOptions.data.results;
  }, [configOptions]);

  const handleSaveConfig = (name: string, isNew: boolean) => {
    const configId = form.getFieldValue('segmentationConfig.id');
    const segmentationConfigMethod = form.getFieldValue(
      'segmentationConfig.method'
    );
    let url: string;
    let method: 'POST' | 'PATCH';
    if (isNew) {
      url = `/dca_scenarios/segmentation_configs`;
      method = 'POST';
    } else {
      url = `/dca_scenarios/segmentation_configs/${configId}`;
      method = 'PATCH';
    }

    const payload = {
      name,
      method: segmentationConfigMethod!,
      fallbackToPreviousSegment:
        form.getFieldValue('segmentationConfig.fallbackToPreviousSegment') ??
        false,
      targetSegment:
        form.getFieldValue('segmentationConfig.targetSegment') ?? undefined,
      parameters: {} as Record<string, unknown>,
    };

    if (segmentationConfigMethod === 'pelt') {
      payload.parameters = {
        model: form.getFieldValue('segmentationConfig.parameters.model'),
        segmentMinLength: form.getFieldValue(
          'segmentationConfig.parameters.segmentMinLength'
        ),
        penalty: form.getFieldValue('segmentationConfig.parameters.penalty'),
        jumpSize: form.getFieldValue('segmentationConfig.parameters.jumpSize'),
      };
    }

    saveSegmentationConfigMutation({
      url,
      type: method,
      data: payload,
      onSuccess: (data) => {
        onClose();
        form.setFieldValue('segmentationConfig', {
          ...defaultValues?.segmentationConfig,
          id: data.id,
          ...payload,
        } as SegmentationConfig);
        updateFieldDefault('segmentationConfig', {
          ...defaultValues?.segmentationConfig,
          id: data.id,
          ...payload,
        } as SegmentationConfig);
        setRefetchOptions(refetchOptions + 1);
      },
      onError: (error) => {
        if (error?.errors) {
          const errors = error.errors;
          let msg = '';
          errors.forEach((error) => {
            msg += `${error.message}`;
          });
          setError(msg);
        }
      },
    });
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <AccordionSummary>
          <h2 className={commonStyles.configFormTitle}>Segmentation</h2>
        </AccordionSummary>
        <AccordionDetails>
          <div className={commonStyles.configFormContainer}>
            <form.Subscribe
              selector={(state) => {
                const segmentationConfig = state.values.segmentationConfig;
                const isModified =
                  !!segmentationConfig &&
                  !!defaultValues?.segmentationConfig &&
                  isSegmentationConfigModified(
                    segmentationConfig,
                    defaultValues.segmentationConfig
                  );
                const method = state.values.segmentationConfig?.method;

                const fieldMeta = state.fieldMeta;
                const hasErrors = (meta: { errors?: unknown[] } | undefined) =>
                  meta?.errors !== null &&
                  meta?.errors?.length &&
                  meta?.errors?.length > 0;

                let isValid = true;

                if (method === 'pelt') {
                  if (
                    hasErrors(
                      fieldMeta['segmentationConfig.parameters.penalty']
                    ) ||
                    hasErrors(
                      fieldMeta[
                        'segmentationConfig.parameters.segmentMinLength'
                      ]
                    ) ||
                    hasErrors(
                      fieldMeta['segmentationConfig.parameters.jumpSize']
                    )
                  ) {
                    isValid = false;
                  }
                }

                return { isModified, isValid, segmentationConfig, method };
              }}
            >
              {({ isModified, isValid, segmentationConfig, method }) => (
                <>
                  {!segmentationConfig && <ConfigSkeleton id="Segmentation" />}

                  {segmentationConfig && (
                    <>
                      <div className={styles.inputsContainer}>
                        <form.Field name="segmentationConfig.id">
                          {(field) => (
                            <TanstackFieldWrapper<string | undefined>
                              field={field}
                            >
                              {(props) => (
                                <DropdownField
                                  id="segmentationConfig.id"
                                  label="Select a config"
                                  titleText="Config (optional)"
                                  items={formattedOptions}
                                  itemToString={(item: any) =>
                                    capitalize(item.name || '')
                                  }
                                  {...props}
                                  onChange={(value: any) => {
                                    const segmentationConfigData = {
                                      id: value.id,
                                      name: value.name,
                                      method: value.method,
                                      targetSegment: value.targetSegment,
                                      fallbackToPreviousSegment:
                                        value.fallbackToPreviousSegment ??
                                        scenarioFormDefaultValues
                                          .segmentationConfig
                                          .fallbackToPreviousSegment,
                                      parameters:
                                        defaultValues?.segmentationConfig
                                          ?.parameters,
                                    };

                                    if (value.method === 'pelt') {
                                      segmentationConfigData.parameters = {
                                        ...segmentationConfigData.parameters,
                                        model: value.parameters.model,
                                        segmentMinLength:
                                          value.parameters.segmentMinLength,
                                        penalty: value.parameters.penalty,
                                        jumpSize: value.parameters.jumpSize,
                                      };
                                    }

                                    form.setFieldValue(
                                      'segmentationConfig',
                                      segmentationConfigData
                                    );
                                    updateFieldDefault(
                                      'segmentationConfig',
                                      segmentationConfigData
                                    );
                                  }}
                                  value={
                                    segmentationConfig.id
                                      ? {
                                          id: segmentationConfig.id,
                                          name:
                                            formattedOptions.find(
                                              (item: any) =>
                                                item.id ===
                                                segmentationConfig.id
                                            )?.name ?? '',
                                        }
                                      : null
                                  }
                                />
                              )}
                            </TanstackFieldWrapper>
                          )}
                        </form.Field>
                        <form.Field name="segmentationConfig.method">
                          {(field) => (
                            <TanstackFieldWrapper<string> field={field}>
                              {(props) => (
                                <DropdownField
                                  id="method"
                                  label="Method"
                                  titleText="Method"
                                  items={['pelt', 'none']}
                                  itemToString={(item: string | null) =>
                                    capitalize(item || '')
                                  }
                                  {...props}
                                />
                              )}
                            </TanstackFieldWrapper>
                          )}
                        </form.Field>

                        {method === 'pelt' && (
                          <>
                            <form.Field name="segmentationConfig.parameters.segmentMinLength">
                              {(field) => (
                                <TanstackFieldWrapper<number | null | undefined>
                                  field={field}
                                >
                                  {(props) => (
                                    <NumberField
                                      id="segmentMinLength"
                                      label="Segment Minimum Length"
                                      {...props}
                                    />
                                  )}
                                </TanstackFieldWrapper>
                              )}
                            </form.Field>

                            <form.Field name="segmentationConfig.parameters.penalty">
                              {(field) => (
                                <TanstackFieldWrapper<number | null | undefined>
                                  field={field}
                                >
                                  {(props) => (
                                    <NumberField
                                      id="penalty"
                                      label="Penalty"
                                      {...props}
                                    />
                                  )}
                                </TanstackFieldWrapper>
                              )}
                            </form.Field>

                            <form.Field name="segmentationConfig.parameters.model">
                              {(field) => (
                                <TanstackFieldWrapper<string | undefined>
                                  field={field}
                                >
                                  {(props) => (
                                    <DropdownField
                                      id="model"
                                      label="Model"
                                      titleText="Model"
                                      items={['l2', 'rbf']}
                                      itemToString={(
                                        item: string | undefined
                                      ) => capitalize(item || '')}
                                      {...props}
                                    />
                                  )}
                                </TanstackFieldWrapper>
                              )}
                            </form.Field>

                            <form.Field name="segmentationConfig.parameters.jumpSize">
                              {(field) => (
                                <TanstackFieldWrapper<number | null | undefined>
                                  field={field}
                                >
                                  {(props) => (
                                    <NumberField
                                      id="jumpSize"
                                      label="Jump Size"
                                      {...props}
                                    />
                                  )}
                                </TanstackFieldWrapper>
                              )}
                            </form.Field>

                            <form.Field name="segmentationConfig.targetSegment">
                              {(field) => (
                                <TanstackFieldWrapper<string> field={field}>
                                  {(props) => (
                                    <DropdownField
                                      id="targetSegment"
                                      label="Target Segment"
                                      titleText="Target Segment"
                                      items={['first', 'last']}
                                      itemToString={(item: string | null) =>
                                        capitalize(item || '')
                                      }
                                      {...props}
                                    />
                                  )}
                                </TanstackFieldWrapper>
                              )}
                            </form.Field>

                            <div className={commonStyles.checkboxContainer}>
                              <form.Field name="segmentationConfig.fallbackToPreviousSegment">
                                {(field) => (
                                  <TanstackFieldWrapper<boolean> field={field}>
                                    {(props) => (
                                      <Checkbox
                                        id="fallbackToPreviousSegment"
                                        labelText="Fallback to Previous Segment"
                                        checked={props.value}
                                        onChange={(
                                          _evt,
                                          { checked }: { checked: boolean }
                                        ) => props.onChange(checked)}
                                      />
                                    )}
                                  </TanstackFieldWrapper>
                                )}
                              </form.Field>
                            </div>
                          </>
                        )}
                      </div>

                      {isModified && (
                        <InlineNotification
                          className={commonStyles.unsavedChangesNotification}
                          lowContrast
                          hideCloseButton
                          kind="info"
                          title="Unsaved changes"
                          subtitle="Make sure to save"
                        />
                      )}
                      <div className={commonStyles.buttonContainer}>
                        <Button
                          size="md"
                          kind="tertiary"
                          onClick={() => {
                            const baseline =
                              defaultValues?.segmentationConfig ?? null;
                            if (baseline === null) return;
                            form.setFieldValue('segmentationConfig', baseline);
                          }}
                          renderIcon={Reset}
                          disabled={!isModified}
                        >
                          Reset
                        </Button>

                        {isModified && (
                          <Button
                            size="md"
                            kind="primary"
                            disabled={!isValid}
                            onClick={() => {
                              const isNewConfig = !segmentationConfig.id;

                              const configName =
                                formattedOptions.find(
                                  (item: any) =>
                                    item.id === segmentationConfig?.id
                                )?.name ?? '';

                              onOpen(isNewConfig, configName);
                            }}
                            renderIcon={Save}
                          >
                            Save
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </form.Subscribe>
          </div>
        </AccordionDetails>
      </Accordion>
      {configModalState.isOpen && (
        <SaveConfigModal
          section="segmentation"
          config={configModalState.config}
          defaultOption={configModalState.defaultOption}
          disableOptions={configModalState.disableOptions}
          onClose={onClose}
          error={error}
          onSave={handleSaveConfig}
        />
      )}
    </>
  );
};
